import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView, useMotionTemplate, useMotionValue, useMotionValueEvent, useTransform } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";
import { RENOVATIONS } from "@/data/site";
import { Reveal, Eyebrow } from "./Reveal";
import { EASE } from "./motion";

const Compare = ({ item }) => {
  const ref = useRef(null);
  const pos = useMotionValue(100); // percent of the "after" image revealed from the left
  const clip = useMotionTemplate`inset(0 ${pos}% 0 0)`;
  const handle = useTransform(pos, (v) => 100 - v);
  const left = useMotionTemplate`${handle}%`;
  const inView = useInView(ref, { once: true, amount: "some" });
  const [dragging, setDragging] = useState(false);
  const [hinted, setHinted] = useState(false);
  const [val, setVal] = useState(0);
  useMotionValueEvent(pos, "change", (v) => setVal(Math.round(100 - v)));

  useEffect(() => {
    if (!inView) return;
    const c = animate(pos, 50, { duration: 1.8, delay: 0.3, ease: EASE, onComplete: () => setHinted(true) });
    return () => c.stop();
  }, [inView, pos]);

  const setFromClient = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    pos.set(Math.min(100, Math.max(0, ((r.right - clientX) / r.width) * 100)));
  };
  const onDown = (e) => { setDragging(true); setHinted(true); e.currentTarget.setPointerCapture(e.pointerId); setFromClient(e.clientX); };
  const onMove = (e) => { if (dragging) setFromClient(e.clientX); };
  const onUp = () => setDragging(false);
  const onKey = (e) => {
    const step = e.shiftKey ? 10 : 2;
    if (e.key === "ArrowLeft") { pos.set(Math.min(100, pos.get() + step)); e.preventDefault(); }
    if (e.key === "ArrowRight") { pos.set(Math.max(0, pos.get() - step)); e.preventDefault(); }
  };

  return (
    <div
      ref={ref}
      className={`relative aspect-[3/2] overflow-hidden bg-stone-200 select-none touch-pan-y ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      data-testid={`compare-${item.id}`}
    >
      <img src={item.before} alt={`${item.title} — before renovation`} className="absolute inset-0 w-full h-full object-cover" draggable={false} data-testid="compare-before-img" />
      <motion.img style={{ clipPath: clip }} src={item.after} alt={`${item.title} — after renovation`} className="absolute inset-0 w-full h-full object-cover" draggable={false} data-testid="compare-after-img" />

      <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white bg-[#161614]/70 backdrop-blur px-2.5 py-1" data-testid="compare-after-label">After</span>
      <span className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white bg-[#161614]/70 backdrop-blur px-2.5 py-1" data-testid="compare-before-label">Before</span>

      <motion.div style={{ left, x: "-50%" }} className="absolute top-0 bottom-0 flex flex-col items-center" aria-hidden="true">
        <span className="w-px flex-1 bg-white shadow-[0_0_0_1px_rgba(0,0,0,.25)]" />
        <span className="w-px flex-1 bg-white shadow-[0_0_0_1px_rgba(0,0,0,.25)]" />
      </motion.div>
      <motion.button
        type="button"
        style={{ left, x: "-50%", y: "-50%" }}
        role="slider" aria-label="Compare before and after" aria-valuemin={0} aria-valuemax={100} aria-valuenow={val}
        onKeyDown={onKey}
        whileTap={{ scale: 0.92 }}
        className="absolute top-1/2 w-14 h-14 rounded-full bg-[#FBFBF9] text-stone-900 border border-stone-300 shadow-[0_12px_30px_-10px_rgba(0,0,0,.5)] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B85C38]"
        data-testid="compare-handle"
      >
        <ChevronsLeftRight size={20} />
        {!hinted && <span className="absolute inset-0 rounded-full border border-[#B85C38] animate-ping" />}
      </motion.button>
      <motion.span initial={{ opacity: 0 }} animate={{ opacity: hinted && !dragging ? 0 : 1 }} transition={{ delay: 2.2 }} className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.3em] text-white bg-[#161614]/70 backdrop-blur px-3 py-1.5" data-testid="compare-hint">
        Drag to compare
      </motion.span>
    </div>
  );
};

export const BeforeAfter = () => (
  <section id="renovation" className="bg-[#161614] text-[#FBFBF9] py-24 lg:py-36 overflow-hidden" data-testid="before-after-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
        <div>
          <Eyebrow className="mb-6 !text-stone-400">Renovation</Eyebrow>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight">Watch the old home become new</h2>
        </div>
        <p className="max-w-sm text-stone-400 font-light leading-relaxed">Slide across the photo to see what a Carvings renovation changes — walls, roof, light and the way you arrive home.</p>
      </Reveal>
      {RENOVATIONS.map((r, i) => (
        <Reveal key={r.id} variant="rise" duration={1.1} delay={i * 0.1} className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-9"><Compare item={r} /></div>
          <div className="lg:col-span-3 lg:pb-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#C29B38]">{r.location} · {r.year}</p>
            <h3 className="font-serif text-2xl lg:text-3xl mt-2" data-testid={`renovation-title-${r.id}`}>{r.title}</h3>
            <p className="mt-4 text-stone-400 font-light text-sm leading-relaxed">{r.note}</p>
            <a href="/contact" className="btn-ghost mt-8 !border-stone-500 !text-[#FBFBF9] hover:!border-[#FBFBF9]" data-testid="renovation-enquire-btn">Plan a renovation</a>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);
